// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetErrorBoundary } from '@/asset/components/asset-error-boundary';
import { useAsset } from '@/asset/hooks/use-asset';
import { getAssetTickets } from '@/audit/api/get-asset-tickets';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import {
  AddIcCallOutlined,
  SupportAgentOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { CreateTicketDialog } from '@/ticket/components/create-ticket-dialog';
import { TicketTableRow } from '@/ticket/components/ticket-table-row';
import { TicketTableStaticHeader } from '@/ticket/components/ticket-table-static-header';
import {
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableContainer,
  Tabs,
  Typography,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_app/assets/$assetKey/tickets')({
  component: AssetTicketsRoute,
});

function AssetTicketsRoute() {
  const { assetKey } = Route.useParams();

  return (
    <AssetErrorBoundary>
      <AssetTicketsContent assetKey={assetKey} />
    </AssetErrorBoundary>
  );
}

function AssetTicketsContent(props: { assetKey: string }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const newTicketDialog = useDisclosure();
  const { data } = useSuspenseQuery({
    queryKey: ['tickets', 'asset', props.assetKey],
    queryFn: () => getAssetTickets(props.assetKey),
    retry: false,
  });
  const { asset } = useAsset(props.assetKey);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const filteredTickets = data.filter((ticket) => {
    if (selectedTab === 0) return true; // All tickets
    if (selectedTab === 1) return ticket.status === 'OPEN'; // Open tickets
    if (selectedTab === 2)
      return ticket.status === 'CLOSED' || ticket.status === 'COMPLETED'; // Closed or Completed tickets
    return false;
  });
  return (
    <Stack>
      <CreateTicketDialog
        isOpen={newTicketDialog.isOpen}
        onClose={newTicketDialog.close}
        forAssetIds={[asset]}
      />
      <HardfinTabCardWrapper
        title="Tickets"
        actions={
          <Button
            size="small"
            startIcon={<AddIcCallOutlined fontSize="small" />}
            onClick={newTicketDialog.open}
          >
            Open ticket
          </Button>
        }
      >
        <Stack spacing={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingLeft: 1,
              '& .Mui-selected': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiTab-root': {
                px: 2,
              },
              '& .MuiTab-root.MuiButtonBase-root': {
                ml: 0,
              },
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Asset ticket filters"
            >
              <Tab label="All" />
              <Tab label="Open" />
              <Tab label="Closed" />
            </Tabs>
          </Box>
          {filteredTickets.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                textAlign: 'center',
                minHeight: '200px',
              }}
            >
              <SupportAgentOutlined
                sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No tickets yet
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: '400px', marginBottom: 3 }}
              >
                Tickets help you manage support requests for this asset
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TicketTableStaticHeader />
                <TableBody>
                  {filteredTickets.map((ticket, index) => (
                    <TicketTableRow
                      key={ticket.key}
                      ticket={ticket}
                      isLastRow={index === filteredTickets.length - 1}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </HardfinTabCardWrapper>
    </Stack>
  );
}
